import moment from 'moment';

/**
 * 그룹 출결 탭이 쓰는 상수와 순수 헬퍼.
 *
 * <p>React 에 의존하지 않는 것만 모았다. 출결 탭 본체(MemberGroupAttendanceTab.jsx)가
 * 1,500줄을 넘어가 뷰 로직과 표기 규칙이 섞여 읽히지 않던 것을 분리한 것이고,
 * 동작은 그대로다.
 */

export const STATUS_LABEL = { RESERVED: '예약', ATTENDED: '출석', ABSENT: '결석' };

/*
 * 지난 수업인데 예약으로 남아 있는 건은 따로 가르지 않는다 — ScheduledService 의
 * hourlyScheduledJobs(6시간마다)가 수업 종료시각이 지난 RESERVED 를 전부 ABSENT 로
 * 바꾼다. 길어야 몇 시간짜리 과도 상태라 상시 칸을 둘 값어치가 없다 (2026-08-27 확인).
 */

/*
 * 표 위 상태 드롭다운 (#92). 패널의 다중 선택과 달리 '이 상태만 본다' 는 한 가지만 고른다.
 * 지각은 status 가 아니라 ATTENDED 행에 붙는 플래그라 여기서만 별도 항목으로 세운다.
 */
// 회차 카드에 한 번에 세울 참여자 이름표 수. 두 줄쯤에서 끊어야 카드가 벽이 되지 않는다.
export const PEOPLE_CHIP_LIMIT = 8;

export const STATUS_VIEW_OPTIONS = [
  { value: '', label: '상태 전체' },
  { value: 'ATTENDED', label: '출석' },
  { value: 'LATE', label: '지각' },
  { value: 'ABSENT', label: '결석' },
  { value: 'RESERVED', label: '예정' },
];

/** 행(또는 회차)이 고른 상태에 해당하는지. 지각은 출석의 부분집합이다. */
export const matchesStatusView = (row, statusView) => {
  if (!statusView) return true;
  if (statusView === 'LATE') return row.status === 'ATTENDED' && row.late === true;
  return row.status === statusView;
};

export const TYPE_LABEL = {
  GROUP_LESSON: '그룹',
  PRIVATE_LESSON: '개인',
  DUET_LESSON: '듀엣',
  OT: 'OT',
};

/*
 * 기간 프리셋 (#93). 오늘·이번 주·이번 달 셋은 어느 지점에나 늘 있고 지울 수 없다
 * (사용자 확정). 그 뒤에 지점이 만든 버튼이 붙는다.
 *
 * '전체'(전 기간)는 두지 않는다 — 출결 조회 API 가 한 번에 366일까지만 받는다
 * (MemberGroupService.MAX_PERIOD_DAYS). 이름과 실제가 어긋나느니 없는 편이 낫다.
 */
export const BUILT_IN_PRESETS = [
  { id: null, key: 'today', label: '오늘', kind: 'TODAY', amount: null },
  { id: null, key: 'thisWeek', label: '이번 주', kind: 'THIS_WEEK', amount: null },
  { id: null, key: 'thisMonth', label: '이번 달', kind: 'THIS_MONTH', amount: null },
];

export const DEFAULT_PRESET_KEY = 'thisMonth';

/*
 * 새로 만드는 버튼은 이름과 기간을 지점이 직접 정한다 (2026-08-27 사용자 확정).
 * '최근 N' 롤링 갈래는 이미 만들어진 버튼을 계속 읽기 위해서만 남아 있다.
 *
 * 366일 상한은 서버(PeriodPresetRule)와 같은 값이어야 한다 — 화면에서만 막으면 API
 * 직접 호출로 뚫리고, 서버에서만 막으면 눌러 보고서야 실패를 안다.
 */
export const PRESET_MAX_RANGE_DAYS = 366;

/*
 * 상대 기간 — '지난 3개월', '이후 3주' 처럼 오늘에서 뻗어 나가는 구간.
 * 고정 날짜와 달리 시간이 지나도 낡지 않는다. 상한은 서버(PeriodPresetUnit)와 같은 값이다.
 */
export const PRESET_DIRECTIONS = [
  { value: 'PAST', label: '지난' },
  { value: 'FUTURE', label: '이후' },
];

export const PRESET_UNITS = [
  { value: 'DAY', label: '일', max: 365, moment: 'day' },
  { value: 'WEEK', label: '주', max: 52, moment: 'week' },
  { value: 'MONTH', label: '개월', max: 11, moment: 'month' },
];

export const presetUnitOf = value => PRESET_UNITS.find(unit => unit.value === value);

/** 상대 기간이 저장 가능한지. 못 쓰면 이유를 돌려준다. */
export const validateRelativePreset = (amount, unitValue) => {
  const unit = presetUnitOf(unitValue);
  const number = Number(amount);
  if (!unit) return '단위를 골라 주세요';
  if (!Number.isInteger(number) || number < 1) return '1 이상으로 넣어 주세요';
  if (number > unit.max) return `${unit.label}은 ${unit.max}까지 넣을 수 있어요`;
  return null;
};

/** 화면에 미리 보여줄 이름. 서버가 붙이는 것과 같은 규칙이어야 한다. */
export const relativePresetLabel = (direction, amount, unitValue) => {
  const dir = PRESET_DIRECTIONS.find(item => item.value === direction);
  const unit = presetUnitOf(unitValue);
  if (!dir || !unit) return '';
  return `${dir.label} ${amount}${unit.label}`;
};

/** 고른 구간이 저장 가능한지. 못 쓰면 이유를 돌려준다. */
export const validatePresetRange = (label, start, end) => {
  if (!label || !label.trim()) return '이름을 입력해 주세요';
  if (!start || !end) return '시작일과 종료일을 골라 주세요';
  const from = moment(start);
  const to = moment(end);
  if (!from.isValid() || !to.isValid()) return '날짜를 다시 골라 주세요';
  if (from.isAfter(to, 'day')) return '시작일이 종료일보다 늦습니다';
  if (to.diff(from, 'day') >= PRESET_MAX_RANGE_DAYS) {
    return `한 번에 볼 수 있는 기간은 ${PRESET_MAX_RANGE_DAYS}일 미만입니다`;
  }
  return null;
};

/** 서버가 준 옵션 한 줄을 화면이 쓰는 프리셋으로. key 는 선택 상태를 가리키는 값이다. */
export const toPreset = option => ({
  id: option.id,
  key: `p-${option.id}`,
  label: option.label,
  kind: option.kind,
  amount: option.amount,
  direction: option.direction,
  unit: option.unit,
  startDate: option.startDate,
  endDate: option.endDate,
});

/**
 * '최근 N개월'이 1일부터인지 오늘 기준인지 이름만으로는 알 수 없다. 실제로 잡히는
 * 날짜를 그대로 적어 준다 (#93). 최근 N 계열은 오늘에서 거꾸로 세는 롤링 구간이다.
 */
export const presetRangeText = preset => {
  const range = presetRangeOf(preset);
  const start = range.start;
  const end = range.end;
  const endFormat = end.isSame(start, 'year') ? 'MM.DD' : 'YYYY.MM.DD';
  return `${start.format('YYYY.MM.DD')} ~ ${end.format(endFormat)}`;
};

export const presetRangeOf = preset => {
  if (preset.kind === 'FIXED_RANGE') {
    return { start: moment(preset.startDate), end: moment(preset.endDate) };
  }
  if (preset.kind === 'RELATIVE_RANGE') {
    const unit = presetUnitOf(preset.unit)?.moment || 'day';
    // 양쪽 다 오늘을 포함한다 — '지난 3개월'은 3개월 전부터 오늘까지다.
    return preset.direction === 'FUTURE'
      ? { start: moment(), end: moment().add(preset.amount, unit) }
      : { start: moment().subtract(preset.amount, unit), end: moment() };
  }
  if (preset.kind === 'TODAY') {
    return { start: moment(), end: moment() };
  }
  if (preset.kind === 'THIS_WEEK') {
    return { start: moment().startOf('isoWeek'), end: moment().endOf('isoWeek') };
  }
  if (preset.kind === 'THIS_MONTH') {
    return { start: moment().startOf('month'), end: moment().endOf('month') };
  }
  if (preset.kind === 'THIS_YEAR') {
    return { start: moment().startOf('year'), end: moment().endOf('year') };
  }
  if (preset.kind === 'LAST_YEAR') {
    const last = moment().subtract(1, 'year');
    return { start: last.clone().startOf('year'), end: last.clone().endOf('year') };
  }
  const unit = preset.kind === 'RECENT_DAY' ? 'day'
    : preset.kind === 'RECENT_WEEK' ? 'week' : 'month';
  // 오늘을 포함해 N 단위. '최근 1일' 이 오늘 하루가 되도록 하루를 되돌린다.
  return { start: moment().subtract(preset.amount, unit).add(1, 'day'), end: moment() };
};

export const entryToneOf = row =>
  row.entrySource === 'FACE' ? 'FACE' : row.entrySource === 'KIOSK' ? 'KIOSK' : 'NONE';

export const entryLabelOf = row => {
  if (row.entrySource === 'KIOSK') return '체크인';
  if (row.entrySource === 'FACE') return '안면인식';
  return '수기 처리';
};

// 이용권/수강권 구분. 안면 입장은 상품을 남기지 않는다.
export const TICKET_TYPE_LABEL = {
  COURSE_PASS: '이용권',
  COURSE_TICKET: '수강권',
  TRIAL: '일일권',
};

// 체류 시간. 분만 쓰면 '135분'처럼 읽기 어려워 시간을 앞세운다.
export const stayLabel = minutes => {
  if (minutes == null) return '-';
  if (minutes < 60) return `${minutes}분`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours}시간 ${rest}분` : `${hours}시간`;
};

// 회차 칩·카드에 쓰는 짧은 날짜. '8/20 (목)' 처럼 요일까지 있어야 주기가 읽힌다.
export const dayDateLabel = value =>
  (value
    ? `${moment(value).format('M/D')} (${['일', '월', '화', '수', '목', '금', '토'][moment(value).day()]})`
    : '-');

// 표에서는 요일이 있어야 '주말에 빠지는 회원' 같은 게 눈에 들어온다.
export const dateWithDay = value => (value ? `${value} (${moment(value).format('dd')})` : '-');

// 개인레슨은 CLASS_NAME 이 비어 있는 경우가 많아 유형 라벨로 대체 표시한다 (dev 실측).
export const classNameOf = row =>
  row.className || (TYPE_LABEL[row.classType] ? `${TYPE_LABEL[row.classType]}레슨` : '-');

// 출석부 칸에 올렸을 때 그날 무슨 일이 있었는지. 수업이 없으면 그냥 들른 날이다.
export const bookTipOf = cell => {
  const lines = cell.items.map(
    item =>
      `${item.startTime || ''} ${classNameOf(item)} · ${STATUS_LABEL[item.status] || item.status}`,
  );
  if (cell.freeEntry) lines.push('센터 입장 (수업 없음)');
  return lines.join('\n');
};

// 주간 칸에 적는 한 줄. 수업이 있으면 시각을, 없으면 그냥 들른 날임을 적는다.
export const bookWeekLabel = cell => {
  const first = cell.items[0];
  if (first?.startTime) return first.startTime;
  return cell.visited > 0 ? '입장' : '-';
};

// 인라인 [] 를 기본값으로 쓰면 매 렌더 새 배열이 되어 파생 useMemo·useEffect 가 매번 돈다.
export const EMPTY_ROWS = [];

// 한 번에 그리는 행 수. 수천 행을 통째로 DOM 에 얹으면 스크롤이 끊긴다(회원 편집 모달과 같은 값).
export const RENDER_CHUNK = 120;

/*
 * 세션 날짜 칩 색 (#92 회원별 개선). 출석/지각/결석은 검증기 통과 팔레트
 * (#14A79C·#E09A26·#E05B4B)의 연한 배경+진한 글자 조합이고, 예정은 점선 테두리라
 * 색이 없어도 구분된다.
 */
export const SESSION_CHIP = {
  att: { bg: '#E6F5F3', color: '#0F766E', border: '#B9E4DF', dashed: false },
  late: { bg: '#FEF3E2', color: '#B45309', border: '#F5D9A8', dashed: false },
  abs: { bg: '#FDEEEC', color: '#C2453A', border: '#F5C6C0', dashed: false },
  plan: { bg: '#FFFFFF', color: '#6B7280', border: '#C4CBD2', dashed: true },
};
